import { useState, useEffect } from 'react';
import { FileText, Plus, Edit2, Trash2, Eye, Printer, X, Calendar, Upload, File } from 'lucide-react';
import API_URL from '../config/api';
import './ConsentManager.css';

const ConsentManager = () => {
    const [consents, setConsents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [editingConsent, setEditingConsent] = useState(null);
    const [deletingConsent, setDeletingConsent] = useState(null);
    const [formDescription, setFormDescription] = useState('');
    const [pdfFile, setPdfFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchConsents();
    }, []);

    const fetchConsents = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/consent-catalog`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setConsents(data);
            }
        } catch (error) {
            console.error('Error fetching consents:', error);
        } finally {
            setLoading(false);
        }
    };

    const openAddModal = () => {
        setEditingConsent(null);
        setFormDescription('');
        setPdfFile(null);
        setShowModal(true);
    };

    const openEditModal = (consent) => {
        setEditingConsent(consent);
        setFormDescription(consent.description || '');
        setPdfFile(null);
        setShowModal(true);
    };

    const openDeleteModal = (consent) => {
        setDeletingConsent(consent);
        setShowDeleteModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formDescription) return;

        try {
            setSubmitting(true);
            const token = localStorage.getItem('token');
            const url = editingConsent
                ? `${API_URL}/consent-catalog/${editingConsent.id}`
                : `${API_URL}/consent-catalog`;

            const formData = new FormData();
            formData.append('description', formDescription);
            if (pdfFile) {
                formData.append('pdf_file', pdfFile);
            }

            const res = await fetch(url, {
                method: editingConsent ? 'PUT' : 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (res.ok) {
                setShowModal(false);
                setPdfFile(null);
                fetchConsents();
            } else {
                const error = await res.json();
                alert(error.message || 'Error al guardar');
            }
        } catch (error) {
            alert('Error de conexión');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!deletingConsent) return;

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/consent-catalog/${deletingConsent.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                setShowDeleteModal(false);
                setDeletingConsent(null);
                fetchConsents();
            } else {
                const error = await res.json();
                alert(error.message || 'Error al eliminar');
            }
        } catch (error) {
            alert('Error de conexión');
        }
    };

    const handleViewPdf = (consent) => {
        if (consent.pdf_path) {
            const url = consent.pdf_path.startsWith('http') ? consent.pdf_path : `${API_URL}/${consent.pdf_path}`;
            window.open(url, '_blank');
        } else {
            alert('Este consentimiento no tiene un PDF adjunto.');
        }
    };

    const handlePrintPdf = (consent) => {
        if (!consent.pdf_path) {
            alert('Este consentimiento no tiene un PDF adjunto.');
            return;
        }
        const pdfUrl = consent.pdf_path.startsWith('http') ? consent.pdf_path : `${API_URL}/${consent.pdf_path}`;
        const printWindow = window.open(pdfUrl, '_blank');
        if (printWindow) {
            printWindow.addEventListener('load', () => {
                printWindow.print();
            });
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr + 'T12:00:00');
        return date.toLocaleDateString('es-MX', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div className="consent-manager">
            <div className="manager-header">
                <div className="header-info">
                    <FileText size={24} />
                    <div>
                        <h3>Consentimientos</h3>
                        <p>Gestión de consentimientos en PDF para impresión</p>
                    </div>
                </div>
                <button className="btn-primary" onClick={openAddModal}>
                    <Plus size={18} />
                    Nuevo Consentimiento
                </button>
            </div>

            <div className="consent-table-container">
                {loading ? (
                    <div className="loading">Cargando...</div>
                ) : consents.length === 0 ? (
                    <div className="empty-state">
                        <FileText size={48} />
                        <p>No hay consentimientos registrados</p>
                        <button onClick={openAddModal}>Agregar el primero</button>
                    </div>
                ) : (
                    <table className="consent-table">
                        <thead>
                            <tr>
                                <th>Descripción</th>
                                <th>PDF</th>
                                <th>Fecha</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {consents.map(consent => (
                                <tr key={consent.id}>
                                    <td className="description-cell">
                                        {consent.description}
                                    </td>
                                    <td className="pdf-cell">
                                        {consent.pdf_path ? (
                                            <span className="pdf-badge has-pdf">
                                                <File size={14} />
                                                PDF
                                            </span>
                                        ) : (
                                            <span className="pdf-badge no-pdf">
                                                Sin PDF
                                            </span>
                                        )}
                                    </td>
                                    <td className="date-cell">
                                        <Calendar size={14} />
                                        {formatDate(consent.date || consent.created_at)}
                                    </td>
                                    <td className="actions-cell">
                                        {consent.pdf_path && (
                                            <button
                                                className="btn-icon print"
                                                title="Imprimir PDF"
                                                onClick={() => handlePrintPdf(consent)}
                                            >
                                                <Printer size={16} />
                                            </button>
                                        )}
                                        <button
                                            className="btn-icon"
                                            title="Editar"
                                            onClick={() => openEditModal(consent)}
                                        >
                                            <Edit2 size={16} />
                                        </button>
                                        <button
                                            className="btn-icon delete"
                                            title="Eliminar"
                                            onClick={() => openDeleteModal(consent)}
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content consent-form-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingConsent ? 'Editar Consentimiento' : 'Nuevo Consentimiento'}</h2>
                            <button className="close-btn" onClick={() => setShowModal(false)}>
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label>Nombre / Descripción del Consentimiento *</label>
                                    <input
                                        type="text"
                                        value={formDescription}
                                        onChange={(e) => setFormDescription(e.target.value)}
                                        placeholder="Ej: Consentimiento informado para toma de muestra"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Archivo PDF</label>
                                    <div className="pdf-upload-area">
                                        <input
                                            type="file"
                                            id="consent-pdf-upload"
                                            accept=".pdf"
                                            onChange={(e) => setPdfFile(e.target.files[0])}
                                            style={{ display: 'none' }}
                                        />

                                        {pdfFile ? (
                                            <div className="pdf-selected">
                                                <File size={20} />
                                                <span>{pdfFile.name}</span>
                                                <button type="button" className="btn-remove-file" onClick={() => setPdfFile(null)}>
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        ) : (
                                            <label htmlFor="consent-pdf-upload" className="pdf-upload-label">
                                                <Upload size={24} />
                                                <span className="upload-text">
                                                    {editingConsent?.pdf_path ? 'Subir nuevo PDF (reemplazar actual)' : 'Seleccionar archivo PDF'}
                                                </span>
                                                <span className="upload-hint">Haz clic para seleccionar un archivo .pdf</span>
                                            </label>
                                        )}

                                        {editingConsent?.pdf_path && !pdfFile && (
                                            <div className="current-pdf-info">
                                                <File size={16} />
                                                <span>PDF actual cargado</span>
                                                <button
                                                    type="button"
                                                    className="btn-view-current"
                                                    onClick={() => handleViewPdf(editingConsent)}
                                                >
                                                    Ver PDF actual
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-submit" disabled={submitting}>
                                    {submitting ? 'Guardando...' : (editingConsent ? 'Guardar Cambios' : 'Guardar')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-content delete-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Confirmar Eliminación</h2>
                            <button className="close-btn" onClick={() => setShowDeleteModal(false)}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="modal-body">
                            <p className="delete-message">¿Está seguro de eliminar el consentimiento?</p>
                            {deletingConsent?.pdf_path && (
                                <p className="delete-warning">El archivo PDF asociado también será eliminado.</p>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button type="button" className="btn-cancel" onClick={() => setShowDeleteModal(false)}>
                                Cancelar
                            </button>
                            <button type="button" className="btn-delete" onClick={handleDelete}>
                                Aceptar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConsentManager;
